import React from 'react';

interface LocationContent {
  type: 'location';
  latitude: number;
  longitude: number;
  address?: string;
  name?: string;
}

interface LocationPreviewProps {
  content: LocationContent;
}

/**
 * Component to display location messages with decorative map preview
 *
 * @example
 * <LocationPreview content={{
 *   type: 'location',
 *   latitude: -23.5505,
 *   longitude: -46.6333,
 *   name: 'São Paulo',
 *   address: 'Av. Paulista, 1000'
 * }} />
 */
export const LocationPreview: React.FC<LocationPreviewProps> = ({ content }) => {
  const { latitude, longitude, name, address } = content;

  // Google Maps URL for external navigation
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

  const openExternal = () => {
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
  };

  const locationName = name ?? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

  return (
    <div data-testid="chat-message-location" onClick={openExternal} className="cursor-pointer">
      <div
        className="w-full max-w-[320px] h-[140px] rounded-[7px] overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, #c7e5f2 0%, #e0f2fe 30%, #d1e9f5 60%, #c8dde9 100%)',
        }}
      >
        <svg className="absolute inset-0 w-full h-full opacity-35" viewBox="0 0 320 140" preserveAspectRatio="none">
          <g stroke="#5e88a8" strokeWidth="0.8" fill="none">
            <path d="M0,40 Q80,30 160,45 T320,50" />
            <path d="M20,90 Q100,80 180,95 T320,100" />
            <path d="M0,120 Q120,110 200,115 T320,125" />
            <path d="M80,0 Q90,40 100,80 T110,140" />
            <path d="M200,0 Q210,50 220,100 T230,140" />
          </g>
        </svg>
        <span
          className="absolute left-1/2 top-1/2 pointer-events-none"
          style={{
            transform: 'translate(-50%, -90%)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            color: '#ef4444',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 8 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
          </svg>
        </span>
      </div>
      <p className="text-[13px] font-semibold m-0 mt-1.5 mb-0.5">{locationName}</p>
      {address && <p className="text-[11.5px] opacity-70 m-0">{address}</p>}
    </div>
  );
};
